import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { toast } from "react-toastify";
import {
  FaYoutube, FaInstagram, FaTwitter, FaTwitch, FaFacebook, 
  FaDownload, FaMusic, FaPlay, FaExclamationTriangle, FaImage, FaSearch, FaHandPointer, FaFileDownload, FaClipboardList
} from "react-icons/fa";
import { SiRumble, SiDailymotion, SiBilibili, SiTiktok, SiTelegram, SiVimeo } from "react-icons/si";

// --- Helper Components ---

const PlatformIcon = ({ icon, label, color }) => (
  <div className="flex flex-col items-center gap-3 px-8 py-4 shrink-0">
    <div className={`text-5xl ${color}`}>{icon}</div>
    <span className="text-sm text-gray-400 whitespace-nowrap">{label}</span>
  </div>
);

const HowToStep = ({ icon, title, desc, step }) => (
  <div className="flex flex-col items-center text-center p-6 bg-gradient-to-b from-white/5 to-transparent rounded-2xl border border-white/10 relative">
    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center font-bold text-sm">
      {step}
    </div>
    <div className="text-4xl text-cyan-400 mb-4 mt-2">{icon}</div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <p className="text-sm text-gray-400">{desc}</p>
  </div>
);

export default function Home() {
  const [url, setUrl] = useState("");
  const [videoInfo, setVideoInfo] = useState(null);
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [quality, setQuality] = useState("720");
  const [audioOnly, setAudioOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [imageError, setImageError] = useState(false);

  const platforms = [
    { icon: <FaYoutube />, label: "YouTube", color: "text-red-500" },
    { icon: <FaInstagram />, label: "Instagram", color: "text-pink-500" },
    { icon: <FaTwitter />, label: "Twitter", color: "text-blue-400" },
    { icon: <FaTwitch />, label: "Twitch", color: "text-purple-500" },
    { icon: <FaFacebook />, label: "Facebook", color: "text-blue-600" },
    { icon: <SiTiktok />, label: "TikTok", color: "text-white" },
    { icon: <SiTelegram />, label: "Telegram", color: "text-blue-400" },
    { icon: <SiBilibili />, label: "Bilibili", color: "text-pink-400" },
    { icon: <SiDailymotion />, label: "Dailymotion", color: "text-indigo-400" },
    { icon: <SiRumble />, label: "Rumble", color: "text-green-500" },
    { icon: <SiVimeo />, label: "Vimeo", color: "text-cyan-400" },
  ];

  const resetState = () => {
    setVideoInfo(null);
    setError(false);
    setProgress(0);
    setImageError(false);
  };

  const fetchInfo = async (e) => {
    e.preventDefault();
    if (!url) return toast.warn("Please enter a URL");

    setIsLoading(true);
    resetState();
    
    try {
      const res = await fetch("https://api.allstreamdownloader.com/api/video-info", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });

      const data = await res.json();
      
      if (res.ok) {
        setVideoInfo(data);
        toast.success("Video found!");
      } else {
        setError(true);
        // Show specific error from backend if available, else generic
        toast.error(data.error || "Failed to analyze URL");
      }
    } catch (error) {
      setError(true);
      toast.error("Oops, something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  const startDownload = async () => {
    setDownloading(true);
    setProgress(0);

    try {
      const res = await fetch("https://api.allstreamdownloader.com/api/download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, quality, audio_only: audioOnly }),
      });

      const { job_id } = await res.json();
      
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(`https://api.allstreamdownloader.com/api/status/${job_id}`);
          const statusData = await statusRes.json();

          if (statusData.status === "completed") {
            clearInterval(pollInterval);
            setProgress(100);
            setDownloading(false);
            
            window.location.href = `https://api.allstreamdownloader.com/api/get-file/${job_id}`;
            toast.success("Download Complete!");
            
            setTimeout(() => {
                setVideoInfo(null);
                setUrl("");
                setProgress(0);
            }, 3000);
            
          } else if (statusData.status === "failed") {
            clearInterval(pollInterval);
            setDownloading(false);
            setError(true);
            // FIX: Show clean error message
            toast.error(statusData.error || "Download failed. Please try again.");
          } else {
            // FIX: Robust progress update
            let prog = statusData.progress || 0;
            if(typeof prog === 'string') prog = parseFloat(prog);
            if(!isNaN(prog)) setProgress(prog);
          }
        } catch (err) {
          console.error("Polling error:", err);
        }
      }, 1000);

    } catch (error) {
      setDownloading(false);
      setError(true);
      toast.error("Failed to start download");
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4 py-12 space-y-16">
      
      {/* HERO SECTION */}
      <section className="text-center space-y-6">
        <motion.h1 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-blue-500 to-purple-500"
        >
          Download From Anywhere
        </motion.h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          The most powerful video and audio downloader supporting 15+ platforms. Fast, Free, and Unlimited.
        </p>
      </section>

      {/* DOWNLOADER TOOL */}
      <motion.section 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="relative w-full bg-gray-900/50 backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-10 shadow-2xl overflow-hidden"
      >
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-cyan-500/10 rounded-full filter blur-2xl"></div>
        <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-purple-500/10 rounded-full filter blur-2xl"></div>

        <form onSubmit={fetchInfo} className="relative z-10 flex flex-col md:flex-row gap-4 mb-6">
          <input
            type="text"
            className="flex-1 px-6 py-4 bg-black/30 border border-white/10 rounded-xl outline-none focus:border-cyan-500 transition-all text-white placeholder-gray-500"
            placeholder="Paste video URL here..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={downloading || isLoading}
          />
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={downloading || isLoading}
            className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                Analyzing...
              </>
            ) : (
              <>
                <FaSearch /> Analyze
              </>
            )}
          </motion.button>
        </form>

        <AnimatePresence mode="wait">
          {error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="py-12 flex flex-col items-center justify-center text-center"
            >
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                <FaExclamationTriangle className="text-3xl text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Oops, something went wrong!</h3>
              <p className="text-gray-400 max-w-md">
                We couldn't process this link. Please ensure the URL is correct and the video is public.
              </p>
              <button 
                onClick={() => { setError(false); setUrl(""); }} 
                className="mt-6 px-6 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Try Again
              </button>
            </motion.div>
          ) : videoInfo ? (
            <motion.div
              key="info"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-6 overflow-hidden"
            >
              <div className="flex flex-col md:flex-row gap-6 bg-black/20 p-4 rounded-2xl border border-white/5">
                <div className="relative w-full md:w-1/3 rounded-xl overflow-hidden bg-gray-800 aspect-video flex items-center justify-center">
                  {!imageError ? (
                    <img 
                      src={videoInfo.thumbnail} 
                      alt="Thumbnail" 
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center text-gray-500 gap-2">
                      <FaImage className="text-4xl" />
                      <span className="text-xs">Thumbnail Unavailable</span>
                    </div>
                  )}
                  
                  {!imageError && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <FaPlay className="text-4xl text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-center">
                  <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">{videoInfo.title}</h3>
                  <p className="text-sm text-gray-400">
                    {videoInfo.duration} • {videoInfo.views} views
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 p-4 rounded-xl">
                  <label className="text-xs text-gray-400 mb-2 block">Video Quality</label>
                  <select
                    value={quality}
                    onChange={(e) => setQuality(e.target.value)}
                    disabled={audioOnly || downloading}
                    className="w-full p-3 bg-black/40 border border-white/10 rounded-lg outline-none focus:border-cyan-500"
                  >
                    <option value="1080">1080p HD</option>
                    <option value="720">720p HD</option>
                    <option value="480">480p</option>
                    <option value="360">360p</option>
                  </select>
                </div>
                
                <div className="bg-white/5 p-4 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FaMusic className="text-purple-400 text-xl" />
                    <div>
                      <p className="text-sm font-medium">Audio Only</p>
                      <p className="text-xs text-gray-500">Download MP3</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={audioOnly} 
                      onChange={(e) => setAudioOnly(e.target.checked)} 
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>

              <div className="pt-4">
                {downloading ? (
                  <div className="w-full max-w-xs mx-auto flex flex-col items-center gap-4">
                    <CircularProgressbar
                      value={progress}
                      text={`${progress.toFixed(0)}%`}
                      styles={buildStyles({
                        pathColor: `rgba(6, 182, 212, ${progress / 100})`,
                        textColor: '#fff',
                        trailColor: 'rgba(255,255,255,0.1)',
                      })}
                    />
                    <p className="text-cyan-300 animate-pulse">Processing your file...</p>
                  </div>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={startDownload}
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-green-500/30 transition-all"
                  >
                    <FaDownload /> Download Now
                  </motion.button>
                )}
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.section>

      {/* --- LOGO SLIDER SECTION --- */}
      <section className="w-full overflow-hidden py-8">
        <h2 className="text-center text-gray-400 text-sm uppercase tracking-widest mb-8">Supported Platforms</h2>
        
        <div className="relative">
          {/* Gradient Overlays for smooth edges */}
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-950 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-950 to-transparent z-10 pointer-events-none"></div>
          
          {/* The scrolling container */}
          <div className="flex animate-scroll w-max">
            {/* Duplicate the array to create the seamless loop effect */}
            {[...platforms, ...platforms].map((p, i) => (
              <div key={i} className="shrink-0 mx-4">
                <PlatformIcon icon={p.icon} label={p.label} color={p.color} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
        <div className="flex items-start gap-4 justify-center text-yellow-400">
          <FaExclamationTriangle className="text-2xl mt-1 shrink-0" />
          <div className="text-left text-sm text-yellow-100/80 space-y-2">
            <h3 className="font-bold text-lg text-yellow-200">Disclaimer</h3>
            <p>
              All Stream Downloader is intended solely for downloading content that is freely available and publicly accessible on the internet. 
              Users must respect the intellectual property rights of content creators and platform terms of service. 
              We do not support or encourage the downloading of copyrighted material for commercial use, nor do we support the downloading of private, paywalled, or premium content without authorization.
            </p>
            <p>
              This tool is provided "as is" for personal archiving purposes only. By using this service, you agree that you are solely responsible for any infringement of copyright or other intellectual property rights. 
              We do not host any videos or audio files on our servers. Please support creators by consuming content through official channels.
            </p>
          </div>
        </div>
      </section>

      {/* HOW TO USE */}
      <section className="space-y-8">
        <h2 className="text-3xl font-bold text-center">How To Use</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <HowToStep icon={<FaClipboardList />} title="Copy Link" desc="Copy the video URL from your browser or app." step="1" />
          <HowToStep icon={<FaHandPointer />} title="Paste Link" desc="Paste the URL into the input box above." step="2" />
          <HowToStep icon={<FaSearch />} title="Select Format" desc="Choose video quality or switch to MP3 audio." step="3" />
          <HowToStep icon={<FaFileDownload />} title="Download" desc="Hit the download button and save your file." step="4" />
        </div>
      </section>

    </div>
  );
}